import React, { useEffect, useState } from "react";

import {
  buildMissingReportAPI,
  fetchAdminSessionsAPI,
  fetchMissingReportsAPI,
  giveCodesAPI,
  searchCodeReceiverAPI,
} from "../levelTestAPI";

// 운영진 전용 세 화면. 레벨테스트 관리 탭 안에 들어간다.
//
// 화면을 감추는 것은 정리용이고 실제 차단은 서버가 한다(`is_admin`).

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

// "2026-08" -> "8월호"
const monthLabel = (month) =>
  month ? `${Number(month.slice(5, 7))}월호` : "전체 회차";

/** 레테를 본 사람 전체 내역. 학원별·온라인을 한 표에서 본다. */
export const AdminSessionTable = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [month, setMonth] = useState("");
  const [academy, setAcademy] = useState("");
  const [keyword, setKeyword] = useState("");
  // 입력할 때마다 부르지 않는다. 돋보기를 누르거나 엔터를 쳐야 찾는다.
  const [searched, setSearched] = useState("");

  useEffect(() => {
    let isCurrent = true;
    setIsLoading(true);
    fetchAdminSessionsAPI({ month, academy, q: searched })
      .then((result) => {
        if (isCurrent) {
          setData(result);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (isCurrent) setIsLoading(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [month, academy, searched]);

  if (!data) {
    return <p className="level-test-empty">{isLoading ? "불러오는 중…" : "내역이 없습니다."}</p>;
  }

  return (
    <>
      <div className="lt-admin-filters">
        <select value={month} onChange={(event) => setMonth(event.target.value)}>
          <option value="">전체 회차</option>
          {data.months.map((one) => (
            <option key={one} value={one}>
              {one.slice(0, 4)}년 {monthLabel(one)}
            </option>
          ))}
        </select>

        <select value={academy} onChange={(event) => setAcademy(event.target.value)}>
          <option value="">전체 학원 + 온라인</option>
          <option value="__online__">온라인 응시 ({data.online_total}건)</option>
          {data.academies.map((one) => (
            <option key={one.academy} value={one.academy}>
              {one.academy} ({one.total}건)
            </option>
          ))}
        </select>

        <form
          className="lt-admin-search"
          onSubmit={(event) => {
            event.preventDefault();
            setSearched(keyword.trim());
          }}
        >
          <input
            value={keyword}
            placeholder="학생 이름"
            onChange={(event) => setKeyword(event.target.value)}
          />
          <button type="submit">찾기</button>
        </form>

        <span className="lt-admin-count">
          {data.total}건{data.shown < data.total && ` (최근 ${data.shown}건 표시)`}
          {isLoading && " · 불러오는 중…"}
        </span>
      </div>

      <div className="level-test-card">
        <div className="level-test-scroll">
          <table className="level-test-table">
            <tbody>
              <tr className="level-test-table__head">
                <td>학생</td>
                <td>학년</td>
                <td>소속</td>
                <td className="is-center">레벨</td>
                <td className="is-center">회차</td>
                <td className="is-center">응시일</td>
                <td className="is-center">점수</td>
                <td className="is-center">응시코드</td>
                <td className="is-center">리포트</td>
              </tr>
              {data.rows.map((row) => (
                <tr key={row.session_id}>
                  <td className="is-strong">{row.name}</td>
                  <td>{row.grade || "-"}</td>
                  <td>
                    {row.is_online ? (
                      <span className="level-test-badge">온라인</span>
                    ) : (
                      row.academy
                    )}
                  </td>
                  <td className="is-center">
                    S{String(row.stage).padStart(2, "0")}
                  </td>
                  <td className="is-center is-muted">{row.set_id.slice(4)}</td>
                  <td className="is-center is-muted">{formatDate(row.started_at)}</td>
                  <td className="is-center">
                    {row.status !== "completed" ? (
                      <span className="level-test-progress">진행중</span>
                    ) : row.score_percent === null ? (
                      <span className="is-muted">-</span>
                    ) : (
                      <b className="level-test-score">{row.score_percent}%</b>
                    )}
                  </td>
                  <td className="is-center">
                    {row.code ? (
                      <span className="level-test-code">{row.code}</span>
                    ) : (
                      <span className="is-muted">-</span>
                    )}
                  </td>
                  <td className="is-center">
                    {row.report_url ? (
                      <a
                        className="level-test-report"
                        href={row.report_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        리포트
                      </a>
                    ) : (
                      <span className="is-muted">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

/** 응시코드를 사람에게 준다. 이름으로 찾아 장수만 고르면 된다. */
export const AdminCodeGive = () => {
  const [keyword, setKeyword] = useState("");
  const [userList, setUserList] = useState([]);
  const [stock, setStock] = useState(null);
  const [picked, setPicked] = useState(null);
  const [count, setCount] = useState(10);
  const [expires, setExpires] = useState("");
  const [isGiving, setIsGiving] = useState(false);
  const [result, setResult] = useState(null);

  // 유효기간 기본값은 석 달 뒤 말일. 코드는 오래 두면 잊혀서 그냥 사라진다.
  useEffect(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth() + 4, 0);
    setExpires(
      `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(
        end.getDate()
      ).padStart(2, "0")}`
    );
  }, []);

  const handleSearch = (event) => {
    event.preventDefault();
    if (keyword.trim().length < 2) {
      alert("두 글자 이상 입력해 주세요.");
      return;
    }
    searchCodeReceiverAPI(keyword.trim())
      .then((data) => {
        setUserList(data.users);
        setStock(data.stock);
        setPicked(null);
        setResult(null);
        if (!data.users.length) {
          alert("그 이름으로 계정을 찾지 못했습니다.");
        }
      })
      .catch(() => alert("계정을 찾지 못했습니다."));
  };

  const handleGive = () => {
    if (!picked) {
      return;
    }
    if (
      !window.confirm(
        `${picked.name || picked.nickname} 님에게 응시코드 ${count}장을 넣습니다.\n유효기간 ${expires}`
      )
    ) {
      return;
    }
    setIsGiving(true);
    giveCodesAPI({ nickname: picked.nickname, count, expires })
      .then((data) => {
        setResult(data);
        setStock(data.stock);
      })
      .catch((error) => {
        alert(error?.response?.data?.error || "발급하지 못했습니다.");
      })
      .finally(() => setIsGiving(false));
  };

  return (
    <div className="level-test-card lt-admin-give">
      <form className="lt-admin-search" onSubmit={handleSearch}>
        <input
          value={keyword}
          placeholder="이름 · 아이디 · 학원명"
          onChange={(event) => setKeyword(event.target.value)}
        />
        <button type="submit">찾기</button>
        {stock !== null && (
          <span className="lt-admin-count">남은 재고 {stock.toLocaleString()}장</span>
        )}
      </form>

      {userList.length > 0 && (
        <div className="lt-admin-users">
          {userList.map((one) => (
            <button
              key={one.nickname}
              type="button"
              className={`lt-admin-user${picked?.nickname === one.nickname ? " lt-admin-user--on" : ""}`}
              onClick={() => {
                setPicked(one);
                setResult(null);
              }}
            >
              <b>{one.name || one.nickname}</b>
              <span className="is-muted">
                {one.organization_name || "학원 미기재"} · {one.nickname}
              </span>
              <span className="level-test-badge">보유 {one.owned}장</span>
            </button>
          ))}
        </div>
      )}

      {picked && (
        <div className="lt-admin-give__form">
          <label>
            장수
            <input
              type="number"
              min="1"
              max="500"
              value={count}
              onChange={(event) => setCount(Number(event.target.value))}
            />
          </label>
          <label>
            유효기간
            <input
              type="date"
              value={expires}
              onChange={(event) => setExpires(event.target.value)}
            />
          </label>
          {/* disabled 로 막지 않는다. 눌러도 아무 일이 없으면 이유를 알 수 없다. */}
          <button type="button" className="level-test-send" onClick={handleGive}>
            {isGiving ? "넣는 중…" : `${picked.name || picked.nickname} 님에게 발급`}
          </button>
        </div>
      )}

      {result && (
        <div className="lt-admin-result">
          <p>
            <b>{result.name || result.nickname}</b> 님에게 <b>{result.codes.length}장</b>{" "}
            넣었습니다. (이미 갖고 있던 것 {result.owned_before}장
            {result.made_new > 0 && ` · 새로 찍은 것 ${result.made_new}장`})
          </p>
          <textarea readOnly value={result.codes.join("\n")} rows={6} />
          <p className="is-muted">
            받은 분의 코드함 화면에 바로 뜹니다. 위 목록을 복사해 전해 주셔도 됩니다.
          </p>
        </div>
      )}
    </div>
  );
};

/** 리포트가 없는 응시. 주인을 못 찾은 것은 아무도 못 굽기 때문에 여기서 굽는다. */
export const AdminMissingReports = () => {
  const [list, setList] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = () =>
    fetchMissingReportsAPI()
      .then((data) => setList(data.list))
      .catch(() => setList([]));

  useEffect(() => {
    load();
  }, []);

  const handleBuild = (sessionId, name) => {
    if (!window.confirm(`${name} 학생의 리포트를 만듭니다. 1~3분쯤 걸립니다.`)) {
      return;
    }
    setBusyId(sessionId || "all");
    buildMissingReportAPI(sessionId)
      .then((data) => {
        alert(
          `${data.started}건을 만들기 시작했습니다.` +
            (data.done ? ` (이미 있던 것 ${data.done}건)` : "") +
            "\n다 되면 목록에서 사라집니다. 잠시 뒤 새로고침해 주세요."
        );
      })
      .catch((error) => alert(error?.response?.data?.error || "만들지 못했습니다."))
      .finally(() => setBusyId(""));
  };

  if (!list) {
    return <p className="level-test-empty">불러오는 중…</p>;
  }
  if (!list.length) {
    return <p className="level-test-empty">리포트가 없는 응시가 없습니다.</p>;
  }

  return (
    <>
      <div className="lt-admin-filters">
        <span className="lt-admin-count">
          리포트가 없는 응시 {list.length}건. <b>주인 없음</b>은 원장님도 코드함에서
          만들 수 없어 여기서 만들어야 합니다.
        </span>
        <button
          type="button"
          className="level-test-send"
          onClick={() => handleBuild("", `${list.length}명`)}
        >
          {busyId === "all" ? "만드는 중…" : "전부 만들기"}
        </button>
      </div>

      <div className="level-test-card">
        <div className="level-test-scroll">
          <table className="level-test-table">
            <tbody>
              <tr className="level-test-table__head">
                <td>학생</td>
                <td>학원</td>
                <td className="is-center">회차</td>
                <td className="is-center">응시일</td>
                <td className="is-center">응시코드</td>
                <td className="is-center">코드 주인</td>
                <td className="is-center">리포트</td>
              </tr>
              {list.map((row) => (
                <tr key={row.session_id}>
                  <td className="is-strong">{row.name}</td>
                  <td>{row.academy || <span className="level-test-badge">온라인</span>}</td>
                  <td className="is-center is-muted">{row.set_id}</td>
                  <td className="is-center is-muted">{formatDate(row.finished_at)}</td>
                  <td className="is-center">
                    {row.code ? (
                      <span className="level-test-code">{row.code}</span>
                    ) : (
                      <span className="is-muted">-</span>
                    )}
                  </td>
                  <td className="is-center">
                    {row.has_owner ? (
                      <span className="is-muted">있음</span>
                    ) : (
                      <b className="lt-admin-warn">주인 없음</b>
                    )}
                  </td>
                  <td className="is-center">
                    <button
                      type="button"
                      className="level-test-make"
                      onClick={() => handleBuild(row.session_id, row.name)}
                    >
                      {busyId === row.session_id ? "만드는 중…" : "리포트 생성"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
